// The versioned notes (lane B7 S14; CR-025, plan D13): the summary's notes and the room's debrief preparation. Nothing
// generates them: a person writes them, "Human decision remains.", and every save appends a version, Edit and Correct
// alike, with the source evidence the note cites ("Summary links to source evidence."). A dismissed note is not shown.
// The dismissal control waits for its word: his verb for it is on the list of words that must never render (see the
// slice report), so this slice draws Edit and Correct only and keeps the dismissal for the server's own record.

import { useState } from "react";
import { ACTION_LABELS, Banner, Button, Card, Field, G1_LABELS, SURFACE_LABELS, Tag, TextArea, type Hs } from "@/mc";
import type { NoteOut } from "../room/types";
import { SUMMARY_LABELS, SUMMARY_RULES } from "./labels";

export type NoteMode = "edit" | "correct";

export interface NotesProps {
  /** The card's title: his feature name (*Summary*, *Debrief preparation*). */
  title: string;
  note?: NoteOut | null | undefined;
  /** This person may write a version (`can.save_notes` / `can.save_preparation_notes`). */
  canWrite: boolean;
  busy?: boolean | undefined;
  error?: string | null | undefined;
  onSave?: ((body: string, mode: NoteMode) => void) | undefined;
  saveHs?: Hs | undefined;
  /** On the Edit control (a card without a link emits no hotspot of its own). */
  hs?: Hs | undefined;
}

export function Notes({ title, note, canWrite, busy, error, onSave, saveHs, hs }: NotesProps) {
  const live = note && note.state !== "dismissed" ? note : null;
  const [mode, setMode] = useState<NoteMode | null>(null);
  const [draft, setDraft] = useState(live?.body ?? "");
  const refs = live?.source_refs ?? [];
  const start = (next: NoteMode) => { setDraft(live?.body ?? ""); setMode(next); };
  const actions = canWrite && mode === null ? (
    <>
      <Button tone="ghost" sm hs={hs} onClick={() => start("edit")}>{SURFACE_LABELS.edit}</Button>
      {live ? <Button tone="ghost" sm onClick={() => start("correct")}>{SUMMARY_LABELS.correct}</Button> : null}
    </>
  ) : null;
  return (
    <Card title={title} actions={actions}>
      <div className="mc-stack">
        {error ? <Banner tone="red" live>{error}</Banner> : null}
        {mode ? (
          <Field label={mode === "correct" ? SUMMARY_LABELS.correct : SURFACE_LABELS.edit} hint={G1_LABELS.humanDecision}>
            <TextArea rows={4} value={draft} onChange={(event) => setDraft(event.target.value)} />
          </Field>
        ) : (
          <Field label={title} hint={G1_LABELS.humanDecision} mode="view">
            <TextArea rows={4} value={live?.body ?? ""} readOnly />
          </Field>
        )}
        {refs.length > 0 ? (
          <div>
            <p className="mc-hint">{SUMMARY_RULES.linksToEvidence}</p>
            {refs.map((ref) => <Tag key={`${ref.scorecard_id}:${ref.revision_id ?? ""}`}>{ref.scorecard_id}</Tag>)}
          </div>
        ) : null}
        {mode ? (
          <div>
            <Button tone="primary" sm busy={busy} hs={saveHs} onClick={() => { onSave?.(draft, mode); setMode(null); }}>
              {ACTION_LABELS.save}
            </Button>
            <Button tone="ghost" sm onClick={() => setMode(null)}>{ACTION_LABELS.cancel}</Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}
